import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { Typography } from '@euler/app/components/typography';
import {
  useAiChatActions,
  useAiChatMessage,
} from '@euler/app/flows/ai-suite/chat/AiChatActions';
import {
  CardNotice,
  ChatCard,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import {
  PrimaryButton,
  SecondaryButton,
} from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { HttpError } from '@euler/lib/request/HttpError';
import {
  CoachingAttemptView,
  CoachingCustomerGender,
  CoachingSetupCard,
  CoachingSetupCardKind,
} from '@euler/model/coaching';
import { useServiceFactory } from '@euler/services/factory';
import { Play, Target } from 'lucide-react-native';
import { memo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import {
  channelName,
  formatName,
  levelName,
  openingName,
  turnsLabel,
} from '../copy';
import { AssumedItems, Bullets } from './CardBits';

type Summary = Extract<
  CoachingSetupCard,
  { kind: CoachingSetupCardKind.Summary }
>;

type StartProblem = 'changed' | 'failed';

/** 自动 leaves the gender to the case: each variation keeps its own. */
const kGenders: [CoachingCustomerGender | null, string][] = [
  [null, '自动'],
  [CoachingCustomerGender.Male, '先生'],
  [CoachingCustomerGender.Female, '女士'],
];

/**
 * Everything settled: the trainee starts practice from here. Any summary
 * may be pressed, not just the latest reply's: the server starts it only
 * while the setup is still what this card shows.
 */
export const SummaryCard = memo(({ card }: { card: Summary }) => {
  const { rpx } = useResponsiveContext();
  const chat = useAiChatActions();
  const { latest } = useAiChatMessage();
  const { coachingService } = useServiceFactory();
  const appNavigation = useAppNavigation();
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState<CoachingAttemptView | null>(null);
  const [problem, setProblem] = useState<StartProblem | null>(null);
  // The customer the trainee will talk to: the case's, unless picked.
  const [gender, setGender] = useState<CoachingCustomerGender | null>(null);
  // A second tap before the re-render that disables the button would
  // start (and, for a story, bill) a second time.
  const inFlight = useRef(false);
  const openPractice = (attempt: CoachingAttemptView) =>
    appNavigation.navigate('Coaching', {
      screen: 'practice',
      params: { attemptId: attempt.id },
    });
  const start = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setStarting(true);
    setProblem(null);
    try {
      const attempt = await coachingService.startSetup(
        card.setupId,
        card.settledKey,
        gender,
      );
      setStarted(attempt);
      openPractice(attempt);
    } catch (error) {
      console.error('coaching start failed', error);
      setProblem(
        error instanceof HttpError && error.subCode === 'coaching_setup_changed'
          ? 'changed'
          : 'failed',
      );
    } finally {
      inFlight.current = false;
      setStarting(false);
    }
  };
  const tiles: [string, string][] = [
    [formatName(card.format), '形式'],
    [
      card.expectedTurns ? turnsLabel(card.expectedTurns) : '按情况而定',
      '时长',
    ],
    [
      card.channel ? channelName(card.channel) : '开始时安排',
      card.openingSide ? `场景 · ${openingName(card.openingSide)}` : '场景',
    ],
  ];
  return (
    <ChatCard
      icon={Target}
      color={aiColors.green}
      title={`${card.title} · ${levelName(card.difficulty)}`}
      subtitle={card.summary}
      tinted
    >
      <View style={{ gap: rpx(14), padding: rpx(14) }}>
        <View style={{ flexDirection: 'row', gap: rpx(8) }}>
          {tiles.map(([value, label]) => (
            <View
              key={label}
              style={{
                flex: 1,
                alignItems: 'center',
                borderRadius: rpx(8),
                backgroundColor: aiColors.muted,
                padding: rpx(8),
              }}
            >
              <Typography.Text
                size={rpx(12)}
                bold
                color={aiColors.foreground}
                numberOfLines={1}
              >
                {value}
              </Typography.Text>
              <Typography.Text size={rpx(10)} color={aiColors.mutedForeground}>
                {label}
              </Typography.Text>
            </View>
          ))}
        </View>
        {started ? null : (
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
              客户
            </Typography.Text>
            {kGenders.map(([value, label]) => {
              const on = gender === value;
              return (
                <Pressable
                  key={label}
                  accessibilityRole="radio"
                  accessibilityState={{ checked: on }}
                  disabled={starting}
                  onPress={() => setGender(value)}
                  style={{
                    paddingHorizontal: rpx(14),
                    paddingVertical: rpx(5),
                    borderRadius: rpx(999),
                    borderWidth: 1,
                    borderColor: on ? aiColors.primary : aiColors.border,
                    backgroundColor: on ? aiColors.primary : aiColors.card,
                  }}
                >
                  <Typography.Text
                    size={rpx(13)}
                    medium={on || undefined}
                    color={
                      on ? aiColors.primaryForeground : aiColors.foreground
                    }
                  >
                    {label}
                  </Typography.Text>
                </Pressable>
              );
            })}
          </View>
        )}
        <Bullets title="你已知道" items={card.traineeKnows} />
        <AssumedItems items={card.assumed} />
        {started ? (
          <View style={{ gap: rpx(8) }}>
            <CardNotice text="已开始，这次练习在「我的练习」里。" />
            <PrimaryButton
              title="继续练习"
              icon={Play}
              onPress={() => openPractice(started)}
            />
          </View>
        ) : problem === 'changed' ? (
          <CardNotice text="这之后练习内容又改过了，请从最新的总结开始。" />
        ) : (
          <View style={{ gap: rpx(8) }}>
            {problem === 'failed' ? (
              <CardNotice tone="danger" text="没能开始，请稍后再试。" />
            ) : null}
            <PrimaryButton
              title="开始练习"
              icon={Play}
              loading={starting}
              disabled={chat.busy}
              onPress={start}
            />
            {latest ? (
              <SecondaryButton title="改一下" onPress={chat.focusComposer} />
            ) : null}
          </View>
        )}
      </View>
    </ChatCard>
  );
});
